import { ArrowLeft, User, Users, Building2, ChevronRight } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'

interface RecipientSelectionScreenProps {
  onNext: () => void
  onBack: () => void
  fromPayByEmail?: boolean
}

const RecipientSelectionScreen = ({
  onNext,
  onBack,
  fromPayByEmail = false,
}: RecipientSelectionScreenProps) => {
  // Always disable "Myself" when coming from "Pay by email" option for any payment method
  const disableMyself = fromPayByEmail

  const recipientTypes = [
    {
      icon: User,
      title: 'Myself',
      description: 'Transfer to your own account',
      onClick: onNext,
      disabled: disableMyself,
    },
    {
      icon: Users,
      title: 'Someone else',
      description: 'Transfer to another person',
      onClick: onNext,
      disabled: false,
    },
    {
      icon: Building2,
      title: 'Business or charity',
      description: 'Transfer to a business or organization',
      onClick: onNext,
      disabled: false,
    },
  ]

  return (
    <div className="h-[600px] flex flex-col">
      <div className="p-4 space-y-2 flex-1 flex flex-col">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-lg font-bold text-gray-900">
            Who's your recipient?
          </h1>
        </div>

        {/* Recipient Types - Main content area */}
        <div className="flex-1 space-y-2">
          {recipientTypes.map((type, index) => (
            <Card
              key={index}
              className={`p-3 transition-shadow border-gray-200 ${
                type.disabled
                  ? 'opacity-50 cursor-not-allowed'
                  : 'cursor-pointer hover:shadow-md'
              }`}
              onClick={type.disabled ? undefined : type.onClick}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      type.disabled ? 'bg-gray-100' : 'bg-gray-100'
                    }`}
                  >
                    <type.icon
                      className={`w-4 h-4 ${type.disabled ? 'text-gray-400' : 'text-gray-600'}`}
                    />
                  </div>
                  <div className="text-left">
                    <h3
                      className={`font-medium text-sm ${
                        type.disabled ? 'text-gray-400' : 'text-gray-900'
                      }`}
                    >
                      {type.title}
                    </h3>
                    <p
                      className={`text-sm ${type.disabled ? 'text-gray-400' : 'text-gray-600'}`}
                    >
                      {type.description}
                    </p>
                  </div>
                </div>
                <ChevronRight
                  className={`w-5 h-5 ${type.disabled ? 'text-gray-300' : 'text-gray-400'}`}
                />
              </div>
            </Card>
          ))}
        </div>

        {/* Back Button */}
        <div className="pt-1 space-y-2">
          <Button
            variant="outline"
            onClick={onBack}
            className="w-full flex items-center justify-center space-x-2 text-gray-700 hover:text-gray-900 border border-gray-300 hover:bg-yellow-50 hover:border-yellow-200 transition-colors h-10"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </Button>
        </div>
      </div>

      {/* Powered by Paytrie - Swapped order */}
      <div className="text-center p-2 border-t border-gray-100">
        <p className="text-xs text-gray-500 mb-1">Powered by Paytrie</p>
        <img
          src="/paytrie-logo.png"
          alt="Paytrie"
          className="h-6 w-auto mx-auto"
        />
      </div>
    </div>
  )
}

export default RecipientSelectionScreen
